import React, { useState } from 'react';

export type GameTheme = 'archer' | 'tank';

interface Props {
  onSelect: (theme: GameTheme) => void;
}

const GameSelector: React.FC<Props> = ({ onSelect }) => {
  const [hovered, setHovered] = useState<GameTheme | null>(null);

  return (
    <div className='cca-gs-root'>
      <div className='cca-gs-stars' aria-hidden='true' />

      <div className='cca-gs-content'>
        <div className='cca-gs-header'>
          <div className='cca-gs-logo'>🎯</div>
          <h1 className='cca-gs-title'>Choose Your Challenge</h1>
          <p className='cca-gs-subtitle'>Same assessment — different game experience</p>
        </div>

        <div className='cca-gs-cards'>
          {/* Archer card */}
          <button
            className={`cca-gs-card cca-gs-card--archer ${hovered === 'archer' ? 'cca-gs-card--active' : ''}`}
            onMouseEnter={() => setHovered('archer')}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect('archer')}
          >
            <div className='cca-gs-card-bg cca-gs-card-bg--archer' />
            <div className='cca-gs-card-inner'>
              <div className='cca-gs-preview'>
                <svg viewBox='0 0 80 120' width='80' height='120'>
                  <ellipse cx='40' cy='118' rx='18' ry='4' fill='rgba(0,0,0,0.3)' />
                  <rect x='30' y='90' width='10' height='22' rx='4' fill='#1b4332' />
                  <rect x='40' y='90' width='10' height='22' rx='4' fill='#1b4332' />
                  <path d='M22 55 Q20 96 32 110 L48 110 Q58 96 56 55 Z' fill='#2d6a4f' />
                  <rect x='20' y='88' width='38' height='7' rx='2' fill='#5c3317' />
                  <rect x='36' y='87' width='8' height='9' rx='2' fill='#c8a84b' />
                  <circle cx='40' cy='38' r='16' fill='#f4d4a0' />
                  <path d='M29 35 Q31 18 51 18 Q57 35 29 35Z' fill='#1b4332' />
                  <path d='M51 28 C57 18 64 22 60 32 C57 24 54 25 51 28Z' fill='#c41e3a' />
                  <line x1='24' y1='64' x2='4' y2='60' stroke='#f4d4a0' strokeWidth='7' strokeLinecap='round' />
                  <path d='M4 46 Q-8 62 4 78' stroke='#8B5A2B' strokeWidth='4' fill='none' strokeLinecap='round' />
                  <line x1='56' y1='64' x2='72' y2='60' stroke='#f4d4a0' strokeWidth='7' strokeLinecap='round' />
                  <line x1='4' y1='46' x2='36' y2='62' stroke='#d4c088' strokeWidth='1.5' />
                  <line x1='36' y1='62' x2='4' y2='78' stroke='#d4c088' strokeWidth='1.5' />
                  <line x1='2' y1='62' x2='36' y2='62' stroke='#c8a84b' strokeWidth='2.5' strokeLinecap='round' />
                </svg>
              </div>
              <div className='cca-gs-card-body'>
                <span className='cca-gs-badge cca-gs-badge--forest'>🌲 Forest</span>
                <h2 className='cca-gs-card-name'>Robinhood Archer</h2>
                <p className='cca-gs-card-desc'>Medieval forest setting. Draw your bow and fire arrows at the correct answer.</p>
                <ul className='cca-gs-features'>
                  <li>🏹 &nbsp;SVG archer with bow animation</li>
                  <li>✨ &nbsp;Arrow flies in a graceful arc</li>
                  <li>🌙 &nbsp;Dusk forest atmosphere</li>
                </ul>
              </div>
              <div className='cca-gs-cta'>Select Archer →</div>
            </div>
          </button>

          {/* Tank card */}
          <button
            className={`cca-gs-card cca-gs-card--tank ${hovered === 'tank' ? 'cca-gs-card--active' : ''}`}
            onMouseEnter={() => setHovered('tank')}
            onMouseLeave={() => setHovered(null)}
            onClick={() => onSelect('tank')}
          >
            <div className='cca-gs-card-bg cca-gs-card-bg--tank' />
            <div className='cca-gs-card-inner'>
              <div className='cca-gs-preview'>
                <svg viewBox='0 0 130 80' width='130' height='80'>
                  <ellipse cx='65' cy='78' rx='50' ry='4' fill='rgba(0,0,0,0.3)' />
                  <rect x='10' y='52' width='110' height='22' rx='11' fill='#2a2a1e' />
                  {[26,44,62,80,98].map((cx, i) => (
                    <circle key={i} cx={cx} cy='63' r='9' fill='#3d3d2a' stroke='#1a1a10' strokeWidth='1.5' />
                  ))}
                  <path d='M18 30 L112 30 L120 44 L122 52 L8 52 L10 44 Z' fill='#5a6438' />
                  <path d='M30 26 Q32 12 65 10 Q98 12 100 26 L100 30 L30 30 Z' fill='#4e5e2a' />
                  <line x1='65' y1='20' x2='126' y2='16' stroke='#2e3818' strokeWidth='8' strokeLinecap='round' />
                  <line x1='65' y1='19' x2='126' y2='15' stroke='#3e4a24' strokeWidth='3' strokeLinecap='round' />
                  <circle cx='126' cy='16' r='4.5' fill='#252e12' />
                  <ellipse cx='65' cy='12' rx='9' ry='3.5' fill='#3a4520' />
                </svg>
              </div>
              <div className='cca-gs-card-body'>
                <span className='cca-gs-badge cca-gs-badge--desert'>🏜️ Desert</span>
                <h2 className='cca-gs-card-name'>Tank Commander</h2>
                <p className='cca-gs-card-desc'>Desert warzone setting. Aim your cannon and fire shells at the correct answer.</p>
                <ul className='cca-gs-features'>
                  <li>🚀 &nbsp;Detailed 2D realistic tank SVG</li>
                  <li>💥 &nbsp;Shell blast with muzzle flash</li>
                  <li>🌅 &nbsp;Dramatic sunset atmosphere</li>
                </ul>
              </div>
              <div className='cca-gs-cta'>Select Tank →</div>
            </div>
          </button>
        </div>

        <p className='cca-gs-footer'>Both modes cover the same assessment — pick your style</p>
      </div>
    </div>
  );
};

export default GameSelector;
